import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Claim, ClaimType, Product, ReconcileReport, SkuVersion, SkuVersionStatus } from "../api/models"
import { RootState } from "../redux/store"
import { optionalPositiveInt } from "./AddSkuDialog"
import { InstallerFieldsView, useInstallerFields } from "./InstallerFields"
import ProductFilter from "./widgets/ProductFilter"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// the review page's claim grid: locked product pill | seats | per-year |
// expiry | retro (new products only) — the wizard's table shape, without the
// remove column
const reviewGridTemplate = "200px 90px 90px 44px"
const reviewRowStyle: React.CSSProperties = {
  display: "grid", gridTemplateColumns: reviewGridTemplate, gap: 8, alignItems: "center", justifyContent: "center", textAlign: "left"
}
const reviewHeaderStyle: React.CSSProperties = {
  ...reviewRowStyle, color: "var(--nfa-light-grey)", fontSize: "9pt", textAlign: "center"
}

// one editable-terms row of the release review — the product itself is
// locked (set changes belong to Edit Version), the terms are the last-chance
// adjustment before they freeze
interface ReviewRow {
  claim: Claim
  product: Product | null
  seatsText: string
  yearlyText: string
}

// What happens to one test type's licenses (alpha = the org's team, beta =
// the testers) when the version is released.
type Disposition = "keep" | "remove" | "standard" | "nfr"
const testDispositions = (type: "alpha" | "beta") => [
  { value: "keep" as const, label: `Keep as ${type}`, desc: `Holders keep their ${type}-type licenses of this version.` },
  { value: "standard" as const, label: "Convert to Standard", desc: "Their licenses become regular standard licenses." },
  { value: "nfr" as const, label: "Convert to NFR", desc: "Not-for-resale licenses — kept free of charge." },
  { value: "remove" as const, label: "Remove", desc: `Revoke the ${type} licenses — holders lose access${type === "beta" ? ", and any iLok licenses deposited from them are revoked too" : ""}.` },
]

// The forward-only lifecycle transition dialog (draft→alpha→beta→released,
// stages may be skipped). Opened without a preset target (the draft/alpha
// rows' "Promote"), it starts on a STAGE CHOOSER — one tile per reachable
// stage with a one-line map of its consequence, preselecting the natural
// next stage — then flows into the chosen stage's page; with a preset
// target (the beta row's "Release", the only stage left) the chooser is
// skipped. Self-sufficient: when the version is missing required
// installers, the upload fields render INLINE instead of a disabled button
// pointing at Edit; the dialog uploads, PATCHes the version's URLs (and
// retro flags), then posts the transition — each step safe to retry.
//
// To alpha: one page — alpha licenses are deposited to every member of the
// owning organization (no choice: the team is the alpha audience by
// definition), plus the installer fields when missing.
//
// To beta: one page — the auto-deposit choice (deposit beta licenses to the
// whole tester list now and to testers added later, or off = fully quiet)
// plus the installer fields when missing.
//
// To released: page 1 carries the release info, installer fields when
// missing, and a per-test-type license disposition (keep/convert/remove) for
// the alpha holders (the team) and the beta holders (testers) — each only
// when that audience exists; every release with license claims then gets a
// FINAL REVIEW page — the complete claim set with the products locked, the
// terms (seats/per-year/expiry) still editable, and the retro checkboxes
// next to the products new versus the previous release. Deliberately its own
// explicit step: everything on it freezes at release. Only claim-less
// versions release in one page, with the (two-click, AddVersionDialog-
// pattern) release button right there. Ends on a done-step with the
// reconcile + disposition reports. Conditionally mounted.
export type PromoteTarget = SkuVersionStatus.Alpha | SkuVersionStatus.Beta | SkuVersionStatus.Released

export function PromoteVersionDialog({ skuId, version, target, previousReleased, skuShipsProducts, productEntities, accessToken, onClose, onDone }: {
  skuId: string
  version: SkuVersion
  // absent = open on the stage-chooser page (draft/alpha rows' single
  // "Promote" action — the stage is a real choice there and each tile
  // explains its consequence); present = jump straight to that stage's
  // page (the beta row's "Release", where no other stage remains)
  target?: PromoteTarget
  // the sku's current latest RELEASED version (claims included) — the base
  // for the "new plug-ins" retro decision; absent = first release
  previousReleased?: SkuVersion
  // any RELEASED version of the sku carries a license claim — a release then
  // requires installers even if this version's own claims don't (server rule)
  skuShipsProducts: boolean
  // resolves claim product names (the SkuPage cache, hydrated for all
  // versions' claims)
  productEntities: RootState["products"]["entities"]
  accessToken: string
  onClose: () => void
  onDone: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  // seeded with the fixed version number (hidden field) so the file-name
  // validation binds uploads to it
  const installer = useInstallerFields(accessToken, { version: version.version })
  const [betaAutoDeposit, setBetaAutoDeposit] = useState(true)
  // the chosen stage — fixed when `target` came in, otherwise the chooser
  // page's live selection, preseeded with the natural next stage (skips are
  // supported but deliberate)
  const [stage, setStage] = useState<PromoteTarget>(
    target ?? (version.status === SkuVersionStatus.Draft ? SkuVersionStatus.Alpha : SkuVersionStatus.Beta))
  const [page, setPage] = useState<"stage" | "main" | "retro">(target === undefined ? "stage" : "main")
  const [armed, setArmed] = useState(false)
  const [step, setStep] = useState<"form" | "saving" | "done" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")
  const [report, setReport] = useState<ReconcileReport | null>(null)
  const [alphaReport, setAlphaReport] = useState<{ action: string, entitlements: number } | null>(null)
  const [betaReport, setBetaReport] = useState<{ action: string, entitlements: number } | null>(null)
  // member+tester overlap resolved at release: their beta duplicates are
  // revoked server-side so nobody ends up with two permanent licenses
  const [dupBetaRemoved, setDupBetaRemoved] = useState(0)

  // plug-ins NEW versus the previous release — the ones whose retroactivity
  // is decided here, on their own explicit page; pre-seeded from the stored
  // (previously saved) flags
  const prevProductIds = new Set((previousReleased?.claims ?? [])
    .filter(c => c.type === ClaimType.License).map(c => c.productId))
  const newLicenseClaims = previousReleased !== undefined
    ? (version.claims ?? []).filter(c => c.type === ClaimType.License && !prevProductIds.has(c.productId))
    : []
  const [retroIds, setRetroIds] = useState<Set<string>>(() =>
    new Set((version.claims ?? [])
      .filter(c => c.type === ClaimType.License && c.retroactive && previousReleased !== undefined && !prevProductIds.has(c.productId))
      .map(c => c.productId)))
  // the review page's editable terms, seeded from the version's claims —
  // the last chance to adjust seats/per-year before they freeze
  const [reviewRows, setReviewRows] = useState<ReviewRow[]>(() =>
    (version.claims ?? [])
      .filter(c => c.type === ClaimType.License)
      .map(c => ({
        claim: c,
        product: productEntities[c.productId]?.data ?? null,
        seatsText: c.maxActivations != null ? String(c.maxActivations) : "2",
        yearlyText: c.maxYearlyActivations != null ? String(c.maxYearlyActivations) : ""
      })))
  const patchReviewRow = (productId: string, patch: Partial<ReviewRow>) =>
    setReviewRows(rows => rows.map(r => r.claim.productId === productId ? { ...r, ...patch } : r))
  // the per-test-type license dispositions — each only meaningful with
  // holders of that type (alpha = the org's team, beta = the testers)
  const alphaHolders = version.alphaUserCount ?? 0
  const betaHolders = version.betaUserCount ?? 0
  const [alphaLicenses, setAlphaLicenses] = useState<Disposition>("keep")
  const [betaLicenses, setBetaLicenses] = useState<Disposition>("keep")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const busy = step === "saving"
  const toAlpha = stage === SkuVersionStatus.Alpha
  const toBeta = stage === SkuVersionStatus.Beta
  const toRelease = stage === SkuVersionStatus.Released
  // the chooser's tiles — one per reachable stage (forward-only), each a
  // one-line map of the consequence; the detailed contract stays on the
  // stage's own page
  const stageOptions: { value: PromoteTarget, title: string, desc: string }[] = [
    ...(version.status === SkuVersionStatus.Draft ? [{
      value: SkuVersionStatus.Alpha as PromoteTarget, title: "Alpha",
      desc: "Team testing — deposits alpha licenses to everyone in your organization, including you."
    }] : []),
    {
      value: SkuVersionStatus.Beta, title: "Beta",
      desc: "Opens the version to your beta-tester list — the team holds alpha licenses through beta."
    },
    {
      value: SkuVersionStatus.Released, title: "Release",
      desc: "Becomes the SKU's fulfillment target: new buyers get this version and its content freezes permanently."
    },
  ]
  const hasLicenseClaims = (version.claims ?? []).some(c => c.type === ClaimType.License)
  // alpha/beta require installers when the version itself carries plug-ins;
  // a release also when any prior release shipped them (server rules) — when
  // they're missing, the upload fields render inline on page 1
  const requiresInstallers = toRelease ? (hasLicenseClaims || skuShipsProducts) : hasLicenseClaims
  const needsInstallerFields = requiresInstallers && !version.downloadMacUrl
  const macProvided = installer.macFile !== null || installer.macUrl.trim() !== ""
  const winProvided = installer.winFile !== null || installer.winUrl.trim() !== ""
  const installersReady = !needsInstallerFields || (installer.valid && macProvided && winProvided)
  // the explicit final-review page — every release with license claims gets
  // it (terms are editable there, worth a last look even with nothing new);
  // only claim-less (credits-only/empty) versions release in one page
  const hasReviewPage = toRelease && reviewRows.length > 0
  // the wizard's page list: the stage chooser (when no stage was preset),
  // the chosen stage's own page, and the release flow's final review — the
  // counter renders once there is more than one page
  const stepTitles = [
    ...(target === undefined ? ["Stage"] : []),
    toAlpha ? "Alpha" : toBeta ? "Beta" : "Release",
    ...(hasReviewPage ? ["Final review"] : []),
  ]
  const stepIndex = page === "stage" ? 0 : (target === undefined ? 1 : 0) + (page === "retro" ? 1 : 0)
  // the armed-confirm suffix naming every test type whose licenses the
  // chosen dispositions would revoke
  const removedTypes = [
    ...(alphaLicenses === "remove" && alphaHolders > 0 ? ["alpha"] : []),
    ...(betaLicenses === "remove" && betaHolders > 0 ? ["beta"] : []),
  ]
  const removalWarning = removedTypes.length > 0 ? ` and revoke the ${removedTypes.join(" and ")} licenses` : ""
  // the effective retro set at release: continuing plug-ins keep their
  // stored flag, new ones follow the retro page's checkboxes
  const isNewProduct = (productId: string) => previousReleased !== undefined && !prevProductIds.has(productId)
  const effectiveRetroCount = (version.claims ?? []).filter(c =>
    c.type === ClaimType.License && (isNewProduct(c.productId) ? retroIds.has(c.productId) : c.retroactive)).length
  const retroChanged = newLicenseClaims.some(c => c.retroactive !== retroIds.has(c.productId))
  const productName = (productId: string) => productEntities[productId]?.data?.name ?? productId
  // the review split mirrors the wizard: carried-over vs new products (retro
  // applies to new ones only)
  const preReviewRows = reviewRows.filter(r => !isNewProduct(r.claim.productId))
  const newReviewRows = reviewRows.filter(r => isNewProduct(r.claim.productId))
  const termsChanged = reviewRows.some(r =>
    String(r.claim.maxActivations ?? "") !== r.seatsText
    || (r.claim.maxYearlyActivations != null ? String(r.claim.maxYearlyActivations) : "") !== r.yearlyText)
  const reviewValid = reviewRows.every(r =>
    Number.isInteger(Number(r.seatsText)) && Number(r.seatsText) > 0
    && optionalPositiveInt(r.yearlyText))
  const topupCount = (version.claims ?? []).filter(c => c.type === ClaimType.Topup).length

  const handleSubmit = async () => {
    setStep("saving")
    setErrorMessage("")
    try {
      // inline installer upload first, then a PATCH carrying the new URLs
      // and/or the retro flags — the version is still editable and the flags
      // inert until the transition lands, so a failure between any of the
      // steps leaves nothing half-applied
      let uploadedUrls: { downloadMacUrl?: string, downloadWinUrl?: string } | null = null
      if (needsInstallerFields)
        uploadedUrls = await installer.upload(skuId)
      if (uploadedUrls !== null || (toRelease && (retroChanged || termsChanged))) {
        await Api.patchSkuVersion({
          skuId,
          versionId: version.id,
          patch: {
            ...(uploadedUrls !== null ? {
              downloadMacUrl: uploadedUrls.downloadMacUrl ?? null,
              downloadWinUrl: uploadedUrls.downloadWinUrl ?? null
            } : {}),
            ...(toRelease && (retroChanged || termsChanged) ? {
              // the review page's edited terms + retro decisions; topups
              // ride along verbatim (the claims body is the exact set)
              claims: [
                ...reviewRows.map(r => ({
                  type: "license" as const,
                  productId: r.claim.productId,
                  maxActivations: Number(r.seatsText),
                  maxYearlyActivations: r.yearlyText === "" ? undefined : Number(r.yearlyText),
                  retroactive: isNewProduct(r.claim.productId) ? retroIds.has(r.claim.productId) : r.claim.retroactive
                })),
                ...(version.claims ?? []).filter(c => c.type === ClaimType.Topup).map(c => ({
                  type: "topup" as const,
                  productId: c.productId,
                  amount: c.amount!,
                  expiryDays: c.expiryDays
                }))
              ]
            } : {})
          },
          accessToken
        })
      }
      const { reconcile, alphaLicenses: alphaDispositionReport, betaLicenses: betaDispositionReport, duplicateBetaLicensesRemoved } = await Api.postSkuVersionStatus({
        skuId,
        versionId: version.id,
        status: stage,
        ...(toBeta ? { betaAutoDeposit } : {}),
        ...(toRelease && alphaHolders > 0 ? { alphaLicenses } : {}),
        ...(toRelease && betaHolders > 0 ? { betaLicenses } : {}),
        accessToken
      })
      setReport(reconcile)
      setAlphaReport(alphaDispositionReport ?? null)
      setBetaReport(betaDispositionReport ?? null)
      setDupBetaRemoved(duplicateBetaLicensesRemoved ?? 0)
      onDone()
      setStep("done")
    } catch (err) {
      setArmed(false)
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      // the review page's claim grid needs the wizard's width (not yet on
      // the chooser page — widen at the page transition, not per radio)
      style={hasReviewPage && page !== "stage" ? { maxWidth: 780 } : undefined}
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content no-scrollbar" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">{page === "stage" ? "Promote Version" : toAlpha ? "Promote to Alpha" : toBeta ? "Promote to Beta" : "Release Version"}</h2>

        {step === "done" ? (
          <>
            <p>
              {toAlpha
                ? `Version ${version.version} is now in alpha. Deposited ${report?.depositedCount ?? 0} license${report?.depositedCount === 1 ? "" : "s"} to your organization's members — teammates added later get them automatically.`
                : toBeta
                  ? `Version ${version.version} is now in beta.${alphaReport !== null && alphaReport.entitlements > 0
                    ? ` Deposited alpha licenses to ${alphaReport.entitlements} team member${alphaReport.entitlements === 1 ? "" : "s"}.`
                    : ""}${betaAutoDeposit && report !== null
                    ? ` Deposited ${report.depositedCount} license${report.depositedCount === 1 ? "" : "s"} to your testers.`
                    : betaAutoDeposit
                      ? ""
                      : " Testers get licenses when you enable auto-deposit or grant them individually."}`
                  : `Version ${version.version} released.${report !== null && report.depositedCount > 0
                    ? ` Deposited ${report.depositedCount} license${report.depositedCount === 1 ? "" : "s"} across ${report.entitlementsScanned} entitlement${report.entitlementsScanned === 1 ? "" : "s"}.`
                    : ""}${dupBetaRemoved > 0
                      ? ` Removed ${dupBetaRemoved} duplicate beta license${dupBetaRemoved === 1 ? "" : "s"} held by team member${dupBetaRemoved === 1 ? "" : "s"} (their alpha licenses win).`
                      : ""}${[
                      { report: alphaReport, type: "alpha" },
                      { report: betaReport, type: "beta" },
                    ].map(({ report: r, type }) => r !== null && r.entitlements > 0
                      ? r.action === "remove"
                        ? ` Removed ${r.entitlements} ${type} license holder${r.entitlements === 1 ? "'s" : "s'"} licenses.`
                        : ` Converted ${r.entitlements} ${type} license holder${r.entitlements === 1 ? "'s" : "s'"} licenses to ${r.action === "nfr" ? "NFR" : "standard"}.`
                      : "").join("")}`}
            </p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Close</button>
            </div>
          </>
        ) : (
          <>
            {stepTitles.length > 1 && (
              <p className="generate-codes-subtitle">
                Step {stepIndex + 1} of {stepTitles.length} — {stepTitles[stepIndex]}
              </p>
            )}

            {page === "stage" ? (
              <>
                <p>
                  Move v{version.version} forward — stages can be skipped,
                  never revisited.
                </p>
                <div className="generate-codes-form">
                  <div className="lifecycle-options">
                    {stageOptions.map(({ value, title, desc }) => (
                      <label key={value} className={`lifecycle-option${stage === value ? " lifecycle-option-selected" : ""}`}>
                        <input type="radio" name="promote-stage" checked={stage === value} onChange={() => setStage(value)} />
                        <span>
                          <span className="lifecycle-option-title">{title}</span>
                          <span className="lifecycle-option-desc">{desc}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            ) : toAlpha ? (
              <>
                <p>
                  Alpha deposits v{version.version}'s licenses to everyone in
                  your organization — including you — so the team can test it.
                  Teammates who join later get them automatically. The version
                  stays fully editable until you release it.
                </p>
                {needsInstallerFields && (
                  <div className="generate-codes-form" style={{ textAlign: "left" }}>
                    <span className="generate-codes-label">Installers</span>
                    {/* the form's 16px gap is too much air under a section
                        label — pull the info line up to ~4px */}
                    <p className="generate-codes-hint" style={{ textAlign: "left", margin: "-12px 0 0" }}>
                      Required for alpha — your team needs the downloads.
                    </p>
                    <InstallerFieldsView fields={installer} busy={busy} installersRequired hideVersion />
                  </div>
                )}
              </>
            ) : toBeta ? (
              <>
                <p>
                  Beta makes v{version.version} visible to your beta testers.
                  It stays fully editable — installers, plug-in set and
                  version number — until you release it.
                  {/* skipping alpha doesn't skip the team: the beta
                      transition deposits the members' alpha licenses too —
                      say so up front (the alpha dialog warns the same way) */}
                  {version.status === SkuVersionStatus.Draft &&
                    " Alpha licenses are also deposited to everyone in your organization — including you — so the team can test it."}
                </p>
                <div className="generate-codes-form">
                  <label style={{ display: "flex", gap: 8, alignItems: "baseline", cursor: "pointer", textAlign: "left" }}>
                    <input
                      type="checkbox"
                      className="retro-checkbox"
                      checked={betaAutoDeposit}
                      onChange={(e) => setBetaAutoDeposit(e.target.checked)}
                      disabled={busy}
                    />
                    <span>
                      Deposit beta licenses to my testers
                      <span style={{ color: "var(--nfa-light-grey)", fontSize: "9pt", display: "block" }}>
                        Everyone on your beta-tester list gets this version's licenses now,
                        and testers you add later get them automatically. Off = quiet beta.
                      </span>
                    </span>
                  </label>
                  {needsInstallerFields && (
                    <>
                      <span className="generate-codes-label" style={{ textAlign: "left", marginTop: 12 }}>Installers</span>
                      <p className="generate-codes-hint" style={{ textAlign: "left", margin: "-12px 0 0" }}>
                        Required for beta — testers need the downloads.
                      </p>
                      <InstallerFieldsView fields={installer} busy={busy} installersRequired hideVersion />
                    </>
                  )}
                </div>
              </>
            ) : page === "main" ? (
              <>
                <p>
                  Releasing v{version.version} makes it the SKU's fulfillment
                  target: checkouts, grants, codes and subscriptions move to
                  it, and its content becomes permanent.
                </p>

                {needsInstallerFields && (
                  <div className="generate-codes-form" style={{ textAlign: "left" }}>
                    <span className="generate-codes-label">Installers</span>
                    <p className="generate-codes-hint" style={{ textAlign: "left", margin: "-12px 0 0" }}>
                      Required to release — this SKU ships products.
                    </p>
                    <InstallerFieldsView fields={installer} busy={busy} installersRequired hideVersion />
                  </div>
                )}

                {[
                  { type: "alpha" as const, holders: alphaHolders, audience: "your team", value: alphaLicenses, set: setAlphaLicenses },
                  { type: "beta" as const, holders: betaHolders, audience: "testers", value: betaLicenses, set: setBetaLicenses },
                ].map(({ type, holders, audience, value, set }, i) => holders > 0 && (
                  <div key={type} className="generate-codes-form" style={{ textAlign: "left", marginTop: needsInstallerFields || i > 0 ? 16 : 0 }}>
                    <span className="generate-codes-label">
                      {type === "alpha" ? "Alpha" : "Beta"} licenses — {holders} holder{holders === 1 ? "" : "s"} ({audience})
                    </span>
                    {testDispositions(type).map((option) => (
                      <label key={option.value} style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name={`${type}-disposition`}
                          className="nfa-radio"
                          checked={value === option.value}
                          onChange={() => set(option.value)}
                          disabled={busy}
                        />
                        <span>
                          {option.label}
                          <span style={{ color: "var(--nfa-light-grey)", fontSize: "9pt", display: "block" }}>
                            {option.desc}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                ))}

                {!hasReviewPage && effectiveRetroCount > 0 && (
                  <p style={{ color: "var(--nfa-yellow)", fontSize: "10pt" }}>
                    {effectiveRetroCount} retroactive plug-in{effectiveRetroCount === 1 ? "" : "s"} deposit{effectiveRetroCount === 1 ? "s" : ""} to
                    owners of every older version at this moment — that cannot be undone.
                  </p>
                )}
                {!hasReviewPage && armed && (
                  <p style={{ color: "var(--nfa-yellow)", fontSize: "10pt" }}>
                    Are you sure? Click "Confirm release" to make it final{removalWarning}.
                  </p>
                )}
              </>
            ) : (
              <>
                {/* the final review — the COMPLETE claim set one last time
                    before it freezes: products locked (set changes belong to
                    Edit Version), terms still editable, and the retro
                    decision on the new products. Deliberately its own
                    explicit step: these choices are permanent at release. */}
                <p className="generate-codes-hint" style={{ textAlign: "left" }}>
                  The last look before v{version.version}'s conditions freeze:
                  terms are still editable here; the plug-in set itself is not.
                </p>
                {(previousReleased !== undefined ? [
                  { label: "Pre-existing Products", rows: preReviewRows, isNew: false },
                  { label: `New Products vs v${previousReleased.version}`, rows: newReviewRows, isNew: true },
                ] : [
                  // first release: no predecessor to diff against (and no
                  // retro — there are no older owners)
                  { label: "Products", rows: reviewRows, isNew: false },
                ]).map(({ label, rows, isNew }) => rows.length > 0 && (
                  // extra air above the New Products section so the two
                  // tables read as distinct blocks
                  <div key={label} className="generate-codes-field" style={{ marginTop: isNew ? 28 : 12 }}>
                    <span className="generate-codes-label" style={{ textAlign: "center", marginBottom: 8 }}>{label}</span>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={reviewHeaderStyle}>
                        <span>Product</span>
                        <span title="Max activations">Seats</span>
                        <span title="Max activations per year (empty = unlimited)">Per year</span>
                        {isNew ? (
                          <span title="Owners of every OLDER version receive this plug-in's license at release — permanent">Retro</span>
                        ) : (
                          <span />
                        )}
                      </div>
                      {rows.map((row) => (
                        <div key={row.claim.productId} style={reviewRowStyle}>
                          <div className="claims-editor-product">
                            {row.product !== null ? (
                              <ProductFilter selected={row.product} onSelect={() => {}} accessToken={accessToken} clearable={false} />
                            ) : (
                              <span className="skus-filter-chip skus-filter-chip-active icon-chip" style={{ cursor: "default" }}>
                                {productName(row.claim.productId)}
                              </span>
                            )}
                          </div>
                          <input
                            type="number" min={1} step={1}
                            value={row.seatsText}
                            onChange={(e) => patchReviewRow(row.claim.productId, { seatsText: e.target.value })}
                            className="generate-codes-input"
                            disabled={busy}
                            title="Max activations"
                          />
                          <input
                            type="number" min={1} step={1}
                            value={row.yearlyText}
                            onChange={(e) => patchReviewRow(row.claim.productId, { yearlyText: e.target.value })}
                            className="generate-codes-input"
                            disabled={busy}
                            title="Max activations per year (empty = unlimited)"
                            placeholder="unlimited"
                          />
                          {isNew ? (
                            <input
                              type="checkbox"
                              className="retro-checkbox"
                              checked={retroIds.has(row.claim.productId)}
                              onChange={(e) => setRetroIds(prev => {
                                const next = new Set(prev)
                                if (e.target.checked) next.add(row.claim.productId); else next.delete(row.claim.productId)
                                return next
                              })}
                              disabled={busy}
                              title="Owners of every OLDER version receive this plug-in's license at release — permanent"
                              style={{ justifySelf: "center" }}
                            />
                          ) : (
                            <span />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <p className="generate-codes-hint" style={{ textAlign: "left", marginTop: 8 }}>
                  {newReviewRows.length > 0
                    ? "Retro checked = every current owner of an older version receives that plug-in's license at release. Unchecked = new buyers only."
                    : ""}
                  {topupCount > 0 ? ` ${topupCount} credits claim${topupCount === 1 ? "" : "s"} ride along unchanged.` : ""}
                </p>

                {effectiveRetroCount > 0 && (
                  <p style={{ color: "var(--nfa-yellow)", fontSize: "10pt" }}>
                    {effectiveRetroCount} retroactive plug-in{effectiveRetroCount === 1 ? "" : "s"} deposit{effectiveRetroCount === 1 ? "s" : ""} to
                    owners of every older version at this moment — that cannot be undone.
                  </p>
                )}
                {armed && (
                  <p style={{ color: "var(--nfa-yellow)", fontSize: "10pt" }}>
                    Are you sure? Click "Confirm release" to make it final{removalWarning}.
                  </p>
                )}
              </>
            )}

            {/* uploads run at submit time — live progress next to the button */}
            {installer.uploadStatus && (
              <p style={{ color: "var(--nfa-orange)", fontSize: "10pt", margin: "12px 0 0" }}>
                {installer.uploadStatus.label} {installer.uploadStatus.percent}%
              </p>
            )}

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              {/* Back walks one page: retro → main, and main → the chooser
                  when the dialog opened on it (a hasty "Release" click can
                  retreat instead of reopening) */}
              {page === "retro" ? (
                <button onClick={() => { setArmed(false); setPage("main") }} disabled={busy}>Back</button>
              ) : page === "main" && target === undefined ? (
                <button onClick={() => { setArmed(false); setPage("stage") }} disabled={busy}>Back</button>
              ) : null}
              {page === "stage" ? (
                <button className="btn-upgrade" onClick={() => setPage("main")}>Next</button>
              ) : toAlpha || toBeta ? (
                <button
                  className="btn-upgrade"
                  disabled={busy || !installersReady}
                  onClick={handleSubmit}
                >
                  {busy ? <><Spinner size={12} /> Promoting…</> : toAlpha ? "Promote to alpha" : "Promote to beta"}
                </button>
              ) : page === "main" && hasReviewPage ? (
                <button
                  className="btn-upgrade"
                  disabled={busy || !installersReady}
                  onClick={() => { setArmed(false); setPage("retro") }}
                >
                  Next
                </button>
              ) : (
                <button
                  className={`btn-upgrade${armed && !busy ? " btn-armed" : ""}`}
                  disabled={busy || !installersReady || (page === "retro" && !reviewValid)}
                  onClick={() => armed ? handleSubmit() : setArmed(true)}
                >
                  {busy ? <><Spinner size={12} /> Releasing…</>
                    : armed ? "Confirm release" : `Release v${version.version}`}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}

// The draft self-grant ("Test") dialog used to live here — retired: testing
// now goes through the Alpha promotion, which deposits alpha licenses to the
// whole organization (the promoter included).
